import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  AssignableMember,
  CloseProjectReadyToBillResult,
  CreateProjectInput,
  CreateProjectPhaseInput,
  CreateProjectProductInput,
  MigrateProjectHoursResult,
  PaginatedResult,
  Project,
  ProjectPhase,
  ProjectOption,
  ProjectProduct,
  ProjectRate,
  ProjectStatus,
  RecomputeRatesResult,
  SortDirection,
  UpdateProjectInput,
  UpdateProjectPhaseInput,
  UpdateProjectProductInput,
} from '@catalyst-psa/shared';

import { api } from '../../api/client.js';
import { buildQuery } from '../../api/queryString.js';

export interface ProjectListParams {
  page: number;
  pageSize: number;
  search?: string;
  customerId?: string;
  status?: ProjectStatus;
  projectManagerId?: string;
  projectManagerIds?: string[];
  serviceCategoryId?: string;
  assignedToMemberId?: string;
  assignedMemberIds?: string[];
  isBillable?: boolean;
  sortBy?: string;
  sortDir?: SortDirection;
}

const projectKeys = {
  all: ['projects'] as const,
  list: (params: ProjectListParams) => ['projects', 'list', params] as const,
  detail: (id: string) => ['projects', 'detail', id] as const,
  managers: ['projects', 'managers'] as const,
  options: ['projects', 'options'] as const,
  phases: (projectId: string) => ['projects', projectId, 'phases'] as const,
  products: (projectId: string) => ['projects', projectId, 'products'] as const,
  rates: (projectId: string) => ['projects', projectId, 'rates'] as const,
};

export function useProjects(params: ProjectListParams): UseQueryResult<PaginatedResult<Project>> {
  return useQuery({
    queryKey: projectKeys.list(params),
    queryFn: () =>
      api.get<PaginatedResult<Project>>(
        `/projects${buildQuery({
          page: params.page,
          pageSize: params.pageSize,
          search: params.search,
          customerId: params.customerId,
          status: params.status,
          projectManagerId: params.projectManagerId,
          projectManagerIds: params.projectManagerIds?.join(','),
          serviceCategoryId: params.serviceCategoryId,
          assignedToMemberId: params.assignedToMemberId,
          assignedMemberIds: params.assignedMemberIds?.join(','),
          isBillable: params.isBillable,
          sortBy: params.sortBy,
          sortDir: params.sortDir,
        })}`,
      ),
  });
}

export function useProject(id: string): UseQueryResult<Project> {
  return useQuery({
    queryKey: projectKeys.detail(id),
    queryFn: () => api.get<Project>(`/projects/${id}`),
    enabled: Boolean(id),
  });
}

export function useProjectManagers(): UseQueryResult<AssignableMember[]> {
  return useQuery({
    queryKey: projectKeys.managers,
    queryFn: () => api.get<AssignableMember[]>('/projects/managers'),
  });
}

/**
 * Every project as a lightweight option for selection dropdowns (unpaginated), so a
 * picker always includes the project a ticket/time entry is linked to.
 */
export function useProjectOptions(enabled = true): UseQueryResult<ProjectOption[]> {
  return useQuery({
    queryKey: projectKeys.options,
    enabled,
    queryFn: () => api.get<ProjectOption[]>('/projects/options'),
  });
}

export function useCreateProject(): UseMutationResult<Project, unknown, CreateProjectInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProjectInput) => api.post<Project>('/projects', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.all });
    },
  });
}

export interface UpdateProjectArgs {
  id: string;
  input: UpdateProjectInput;
}

export function useUpdateProject(): UseMutationResult<Project, unknown, UpdateProjectArgs> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: UpdateProjectArgs) => api.patch<Project>(`/projects/${id}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.all });
    },
  });
}

export function useDeleteProject(): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/projects/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.all });
    },
  });
}

/** Issues a fresh public portal token, invalidating any previously shared link. */
export function useRegenerateProjectPublicLink(): UseMutationResult<Project, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Project>(`/projects/${id}/public-link/regenerate`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.all });
    },
  });
}

/**
 * Attaches the project's un-billed hours to its linked managed services agreement
 * (hours already on a PSA invoice or billed externally are skipped).
 */
export function useMigrateProjectHoursToAgreement(): UseMutationResult<
  MigrateProjectHoursResult,
  unknown,
  string
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<MigrateProjectHoursResult>(`/projects/${id}/migrate-hours-to-agreement`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.all });
      // Migrated hours now appear on the agreement (time entries, Cat+ usage).
      void queryClient.invalidateQueries({ queryKey: ['agreements'] });
      void queryClient.invalidateQueries({ queryKey: ['time-entries'] });
    },
  });
}

/**
 * Closes the project (status → completed) and moves its linked onboarding record to the
 * "Ready to Bill" delivery phase.
 */
export function useCloseProjectReadyToBill(): UseMutationResult<
  CloseProjectReadyToBillResult,
  unknown,
  string
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<CloseProjectReadyToBillResult>(`/projects/${id}/close-ready-to-bill`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['onboarding'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Project phases
// ---------------------------------------------------------------------------

export function useProjectPhases(projectId: string): UseQueryResult<PaginatedResult<ProjectPhase>> {
  return useQuery({
    queryKey: projectKeys.phases(projectId),
    queryFn: () =>
      api.get<PaginatedResult<ProjectPhase>>(
        `/projects/${projectId}/phases${buildQuery({ page: 1, pageSize: 200, sortBy: 'sequence', sortDir: 'asc' })}`,
      ),
    enabled: Boolean(projectId),
  });
}

export function useCreateProjectPhase(
  projectId: string,
): UseMutationResult<ProjectPhase, unknown, CreateProjectPhaseInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProjectPhaseInput) =>
      api.post<ProjectPhase>(`/projects/${projectId}/phases`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.phases(projectId) });
    },
  });
}

export interface UpdateProjectPhaseArgs {
  id: string;
  input: UpdateProjectPhaseInput;
}

export function useUpdateProjectPhase(
  projectId: string,
): UseMutationResult<ProjectPhase, unknown, UpdateProjectPhaseArgs> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: UpdateProjectPhaseArgs) =>
      api.patch<ProjectPhase>(`/projects/${projectId}/phases/${id}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.phases(projectId) });
    },
  });
}

export function useDeleteProjectPhase(projectId: string): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/projects/${projectId}/phases/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.phases(projectId) });
    },
  });
}

/** Re-resolves the rate on every non-invoiced time entry in a phase. */
export function useRecomputeProjectPhaseRates(
  projectId: string,
): UseMutationResult<RecomputeRatesResult, unknown, string> {
  return useMutation({
    mutationFn: (id: string) =>
      api.post<RecomputeRatesResult>(`/projects/${projectId}/phases/${id}/recompute-rates`, {}),
  });
}

// ---------------------------------------------------------------------------
// Project rates (per service category)
// ---------------------------------------------------------------------------

export function useProjectRates(projectId: string): UseQueryResult<PaginatedResult<ProjectRate>> {
  return useQuery({
    queryKey: projectKeys.rates(projectId),
    queryFn: () =>
      api.get<PaginatedResult<ProjectRate>>(
        `/projects/${projectId}/rates${buildQuery({ page: 1, pageSize: 200 })}`,
      ),
    enabled: Boolean(projectId),
  });
}

// ---------------------------------------------------------------------------
// Project products (fixed-fee)
// ---------------------------------------------------------------------------

export function useProjectProducts(
  projectId: string,
): UseQueryResult<PaginatedResult<ProjectProduct>> {
  return useQuery({
    queryKey: projectKeys.products(projectId),
    queryFn: () =>
      api.get<PaginatedResult<ProjectProduct>>(
        `/projects/${projectId}/products${buildQuery({ page: 1, pageSize: 200, sortBy: 'createdAt', sortDir: 'asc' })}`,
      ),
    enabled: Boolean(projectId),
  });
}

export function useCreateProjectProduct(
  projectId: string,
): UseMutationResult<ProjectProduct, unknown, CreateProjectProductInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProjectProductInput) =>
      api.post<ProjectProduct>(`/projects/${projectId}/products`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.products(projectId) });
    },
  });
}

export interface UpdateProjectProductArgs {
  id: string;
  input: UpdateProjectProductInput;
}

export function useUpdateProjectProduct(
  projectId: string,
): UseMutationResult<ProjectProduct, unknown, UpdateProjectProductArgs> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: UpdateProjectProductArgs) =>
      api.patch<ProjectProduct>(`/projects/${projectId}/products/${id}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.products(projectId) });
    },
  });
}

export function useDeleteProjectProduct(
  projectId: string,
): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/projects/${projectId}/products/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.products(projectId) });
    },
  });
}
